import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../App";
import { toast } from "sonner";
import { X, Check, ChevronRight, RotateCcw, Target, Flame, Clock, AlertTriangle } from "lucide-react";

/* ═══════════════════════════════════════
   DAILY 3 — Morning Modal + Persistent Banner
   ═══════════════════════════════════════ */

// Blocker reasons for end-of-day reflection
const BLOCKER_REASONS = [
  { key: "urgent", label: "Got pulled into something urgent", icon: "🔥" },
  { key: "waiting", label: "Waiting on someone", icon: "⏳" },
  { key: "underestimated", label: "Underestimated the time", icon: "⏰" },
  { key: "scope", label: "Scope changed", icon: "🔄" },
  { key: "meetings", label: "Meetings ate my day", icon: "📅" },
];

/* ── Morning Commitment Modal ── */
export function DailyPriorityModal({ data, onClose, onSave, onSkip, mandatory = false }) {
  const navigate = useNavigate();
  const [priorities, setPriorities] = useState([
    { text: "", linked_task_id: null, linked_brief_id: null },
    { text: "", linked_task_id: null, linked_brief_id: null },
    { text: "", linked_task_id: null, linked_brief_id: null },
  ]);
  const [weeklyGoal, setWeeklyGoal] = useState(data?.weekly_goal || "");
  const [showSuggestions, setShowSuggestions] = useState(true);
  const isMonday = new Date().getDay() === 1;

  // Pre-fill carry-over items
  useEffect(() => {
    if (data?.carryover?.length > 0) {
      const carried = data.carryover.slice(0, 3).map(c => ({
        text: c.text, linked_task_id: c.linked_task_id || null, linked_brief_id: c.linked_brief_id || null,
      }));
      const empty = Array(3 - carried.length).fill(null).map(() => ({ text: "", linked_task_id: null, linked_brief_id: null }));
      setPriorities([...carried, ...empty]);
    }
  }, [data]);

  const selectSuggestion = (text, taskId = null, briefId = null) => {
    const idx = priorities.findIndex(p => !p.text);
    if (idx === -1) return;
    const next = [...priorities];
    next[idx] = { text, linked_task_id: taskId, linked_brief_id: briefId };
    setPriorities(next);
  };

  const updateText = (idx, text) => {
    const next = [...priorities];
    next[idx] = { ...next[idx], text };
    setPriorities(next);
  };

  const clearSlot = (idx) => {
    const next = [...priorities];
    next[idx] = { text: "", linked_task_id: null, linked_brief_id: null };
    setPriorities(next);
  };

  const handleSave = async () => {
    const filled = priorities.filter(p => p.text.trim());
    if (filled.length === 0) { toast.error("Set at least 1 priority"); return; }
    try {
      await api.post("/me/daily-priorities", { priorities: filled, weekly_goal: isMonday && weeklyGoal ? weeklyGoal : undefined });
      toast.success(`${filled.length} priorities set for today`);
      // Defensive: if a caller forgot to pass onSave, still close cleanly
      // instead of throwing "a is not a function" from minified bundle.
      if (typeof onSave === "function") onSave();
      else if (typeof onClose === "function") onClose();
    } catch (e) {
      // Surface the actual server message so silent 500s don't masquerade
      // as generic frontend bugs.
      const detail = e?.response?.data?.detail || e?.response?.statusText || e?.message;
      toast.error(detail ? `Failed to save: ${detail}` : "Failed to save");
    }
  };

  const suggestions = data?.suggestions || {};
  const allSuggestions = [
    ...(data?.carryover || []).map(c => ({ label: `↻ ${c.text}`, text: c.text, task_id: c.linked_task_id, type: "carryover" })),
    ...(suggestions.overdue_tasks || []).map(t => ({ label: `⚠ ${t.title}`, text: t.title, task_id: t.task_id, type: "overdue" })),
    ...(suggestions.today_tasks || []).map(t => ({ label: `📋 ${t.title}`, text: t.title, task_id: t.task_id, type: "today" })),
    ...(suggestions.review_tasks || []).map(t => ({ label: `👀 Review: ${t.title}`, text: `Review: ${t.title}`, task_id: t.task_id, type: "review" })),
    ...(suggestions.upcoming_briefs || []).map(b => ({ label: `🎯 ${b.title} (${b.company_name})`, text: `Prep: ${b.title}`, brief_id: b.brief_id, type: "brief" })),
  ];
  // Deduplicate by text
  const seen = new Set();
  const uniqueSuggestions = allSuggestions.filter(s => { if (seen.has(s.text)) return false; seen.add(s.text); return true; });
  // Remove already-selected
  const availableSuggestions = uniqueSuggestions.filter(s => !priorities.some(p => p.text === s.text));

  const filledCount = priorities.filter(p => p.text.trim()).length;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center" style={{ background: "rgba(0,0,0,.7)", backdropFilter: "blur(8px)" }}>
      <div className="bg-[var(--s1)] border border-[var(--bdr)] rounded-2xl w-full max-w-[520px] mx-4 overflow-hidden" style={{ boxShadow: "0 24px 80px rgba(0,0,0,.6)" }}>
        {/* Header */}
        <div className="px-6 pt-6 pb-3">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <Target size={18} className="text-[#E31937]" />
              <h2 className="text-[16px] font-bold text-white font-heading">What are your 3 priorities today?</h2>
            </div>
            {!mandatory && onClose && <button onClick={onClose} className="text-[#444] hover:text-[var(--t1)] transition-colors p-1"><X size={16} /></button>}
          </div>
          <p className="text-[12px] text-[#555]">What will move the needle? Pick outcomes, not busy work.</p>
        </div>

        {/* Weekly Goal (Monday only) */}
        {isMonday && (
          <div className="px-6 pb-3">
            <div className="p-3 rounded-lg border border-purple-500/15 bg-purple-500/[.03]">
              <label className="text-[10px] text-purple-400 font-semibold tracking-wider font-heading block mb-1.5">WEEKLY GOAL — What's your ONE big outcome this week?</label>
              <input value={weeklyGoal} onChange={e => setWeeklyGoal(e.target.value)}
                placeholder="e.g., Finish Meta Pitch Deck"
                className="w-full px-3 py-2 bg-[var(--s1)] border border-[var(--bdr)] rounded-lg text-white text-[13px] placeholder:text-[#333] focus:outline-none focus:border-purple-500/40" />
            </div>
          </div>
        )}

        {/* Current weekly goal display (non-Monday) */}
        {!isMonday && data?.weekly_goal && (
          <div className="px-6 pb-2">
            <div className="flex items-center gap-2 text-[11px] text-[#555]">
              <span className="text-purple-400">🎯 Weekly goal:</span>
              <span className="text-[#888]">{data.weekly_goal}</span>
              {data.weekly_goal_achieved && <span className="text-green-400">✓</span>}
            </div>
          </div>
        )}

        {/* Priority Slots */}
        <div className="px-6 pb-3 space-y-2">
          {priorities.map((p, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <span className={`w-6 h-6 rounded-lg flex items-center justify-center text-[12px] font-bold flex-shrink-0 ${p.text ? "bg-[var(--r)]/15 text-[#E31937]" : "bg-[var(--s1)] text-[#333]"}`}>
                {idx + 1}
              </span>
              <div className="flex-1 relative">
                <input value={p.text} onChange={e => updateText(idx, e.target.value)}
                  placeholder={idx === 0 ? "Most important thing today..." : idx === 1 ? "Second priority..." : "Third priority (optional)..."}
                  className="w-full px-3 py-2.5 bg-[var(--s1)] border border-[var(--bdr)] rounded-lg text-white text-[13px] placeholder:text-[#333] focus:outline-none focus:border-[#E31937]/40 transition-all" />
                {p.linked_task_id && <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[9px] px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400">linked</span>}
                {p.linked_brief_id && <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[9px] px-1.5 py-0.5 rounded bg-purple-500/10 text-purple-400">brief</span>}
              </div>
              {p.text && (
                <button onClick={() => clearSlot(idx)} className="text-[#333] hover:text-[#888] transition-colors p-1"><X size={14} /></button>
              )}
            </div>
          ))}
        </div>

        {/* Suggestions */}
        {showSuggestions && availableSuggestions.length > 0 && filledCount < 3 && (
          <div className="px-6 pb-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] text-[#555] font-heading tracking-wider">SUGGESTIONS FROM YOUR WORK</span>
              <button onClick={() => setShowSuggestions(false)} className="text-[10px] text-[#333] hover:text-[#666]">hide</button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {availableSuggestions.slice(0, 8).map((s, i) => (
                <button key={i} onClick={() => selectSuggestion(s.text, s.task_id || null, s.brief_id || null)}
                  className={`text-[11px] px-2.5 py-1.5 rounded-lg border transition-all cursor-pointer hover:-translate-y-0.5 ${
                    s.type === "carryover" ? "border-amber-500/20 text-amber-400 bg-amber-500/[.04] hover:bg-amber-500/[.08]" :
                    s.type === "overdue" ? "border-red-500/20 text-red-400 bg-red-500/[.04] hover:bg-red-500/[.08]" :
                    s.type === "review" ? "border-cyan-500/20 text-cyan-400 bg-cyan-500/[.04] hover:bg-cyan-500/[.08]" :
                    s.type === "brief" ? "border-purple-500/20 text-purple-400 bg-purple-500/[.04] hover:bg-purple-500/[.08]" :
                    "border-[var(--bdr)] text-[#888] bg-[var(--s1)] hover:bg-[var(--s1)]"
                  }`}>
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[var(--bdr)] flex items-center justify-between">
          {!mandatory
            ? <button onClick={onSkip || onClose} className="text-[13px] text-[#888] hover:text-[var(--t1)] transition-colors">Skip for today</button>
            : <div></div>}
          <button onClick={handleSave} disabled={filledCount === 0}
            className="px-5 py-2.5 bg-[var(--r)] text-white text-[13px] font-bold rounded-lg hover:bg-[var(--r)] transition-all disabled:opacity-30 disabled:cursor-not-allowed font-heading">
            Set {filledCount} {filledCount === 1 ? "Priority" : "Priorities"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Persistent Banner (shows on My Day when priorities are set) ── */
export function DailyPriorityBanner({ todayData, onToggleComplete, onReflect }) {
  const navigate = useNavigate();
  if (!todayData || !todayData.priorities?.length) return null;

  const priorities = todayData.priorities;
  const completed = priorities.filter(p => p.completed).length;
  const total = priorities.length;

  return (
    <div className="mb-3 rounded-xl border border-[var(--bdr)] bg-[var(--s1)] overflow-hidden" style={{ animation: "dmFadeUp .4s ease-out" }}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-[var(--bdr)]">
        <div className="flex items-center gap-2">
          <Target size={14} className="text-[#E31937]" />
          <span className="text-[11px] font-semibold text-[#888] font-heading tracking-wider">TODAY'S PRIORITIES</span>
          <span className="text-[10px] px-2 py-0.5 rounded-full" style={{
            background: completed === total ? "rgba(34,197,94,.1)" : "rgba(227,25,55,.1)",
            color: completed === total ? "#22C55E" : "#E31937",
          }}>{completed}/{total}</span>
        </div>
        {completed < total && (
          <button onClick={onReflect} className="text-[10px] text-[#444] hover:text-[#888] transition-colors">
            What's blocking you?
          </button>
        )}
      </div>
      {/* Priority items */}
      <div className="px-4 py-2 space-y-1">
        {priorities.map((p, idx) => (
          <div key={idx} className="flex items-center gap-3 py-1.5 group">
            <button onClick={() => onToggleComplete(idx)}
              className={`w-5 h-5 rounded-md border flex items-center justify-center flex-shrink-0 transition-all cursor-pointer ${
                p.completed ? "bg-green-500/15 border-green-500/40" : "border-[var(--bdrh)] hover:border-green-400 hover:bg-green-400/10"
              }`}>
              {p.completed && <Check size={12} className="text-green-400" />}
            </button>
            <span className={`text-[13px] flex-1 ${p.completed ? "text-[#555] line-through" : "text-white/90"}`}>
              {p.text}
            </span>
            {p.linked_task_id && (
              <button onClick={() => navigate(`/tasks/${p.linked_task_id}`)}
                className="text-[9px] px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400 opacity-0 group-hover:opacity-100 transition-opacity">
                open →
              </button>
            )}
            {p.linked_brief_id && (
              <button onClick={() => navigate(`/bizdev/briefs/${p.linked_brief_id}`)}
                className="text-[9px] px-1.5 py-0.5 rounded bg-purple-500/10 text-purple-400 opacity-0 group-hover:opacity-100 transition-opacity">
                open →
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Reflection Modal (2:30pm nudge or manual trigger) ── */
export function ReflectionModal({ todayData, onClose, onSave }) {
  const [reason, setReason] = useState(null);
  const [reflection, setReflection] = useState("");

  const incomplete = (todayData?.priorities || []).filter(p => !p.completed);
  const completed = (todayData?.priorities || []).filter(p => p.completed);

  const handleSave = async () => {
    try {
      await api.put("/me/daily-priorities/reflect", { reason, reflection });
      toast.success("Reflection saved");
      onSave();
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center" style={{ background: "rgba(0,0,0,.7)", backdropFilter: "blur(8px)" }}>
      <div className="bg-[var(--s1)] border border-[var(--bdr)] rounded-2xl w-full max-w-[440px] mx-4 p-6" style={{ boxShadow: "0 24px 80px rgba(0,0,0,.6)" }}>
        <div className="flex items-center gap-2 mb-4">
          <Clock size={18} className="text-amber-400" />
          <h2 className="text-[15px] font-bold text-white font-heading">Quick check-in</h2>
        </div>

        <p className="text-[12px] text-[#888] mb-4">
          You completed <strong className="text-green-400">{completed.length}/{(todayData?.priorities || []).length}</strong> priorities today.
          {incomplete.length > 0 && <> Still open: {incomplete.map(p => `"${p.text}"`).join(", ")}</>}
        </p>

        {incomplete.length > 0 && (
          <>
            <p className="text-[11px] text-[#555] mb-2 font-heading tracking-wider">WHAT GOT IN THE WAY?</p>
            <div className="flex flex-wrap gap-1.5 mb-4">
              {BLOCKER_REASONS.map(r => (
                <button key={r.key} onClick={() => setReason(r.key)}
                  className={`text-[11px] px-3 py-2 rounded-lg border transition-all ${
                    reason === r.key ? "border-amber-500/30 bg-amber-500/10 text-amber-400" : "border-[var(--bdr)] text-[#666] hover:text-[#999]"
                  }`}>
                  {r.icon} {r.label}
                </button>
              ))}
            </div>
          </>
        )}

        <textarea value={reflection} onChange={e => setReflection(e.target.value)}
          placeholder="Any thoughts? (optional)"
          className="w-full px-3 py-2 bg-[var(--s1)] border border-[var(--bdr)] rounded-lg text-[13px] text-white placeholder:text-[#333] focus:outline-none focus:border-[#E31937]/40 resize-none mb-4" rows={2} />

        <div className="flex justify-between">
          <button onClick={onClose} className="text-[12px] text-[#555] hover:text-[var(--t1)] transition-colors">Skip</button>
          <button onClick={handleSave} className="px-4 py-2 bg-[var(--r)] text-white text-[12px] font-bold rounded-lg hover:bg-[var(--r)] transition-all font-heading">
            Save Reflection
          </button>
        </div>
      </div>
    </div>
  );
}
